import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, last, map, switchMap, tap } from 'rxjs/operators';
import { Document } from 'src/app/core/models/document.model';
import { GedService, MetaTeleversement, dateLocaleIso } from 'src/app/core/services/entities/ged.service';
import { FermeStateService } from 'src/app/core/services/entities/ferme-state.service';
import { compresserImage } from './image-compression';
import { ToastService } from './toast.service';
import { GalerieSelecteurService } from './galerie-selecteur';

/** apercu : URL blob: (à révoquer) ; apercuSur : la même, marquée sûre pour [src] (sinon « unsafe:blob:… ») */
interface FichierEnCours { nom: string; taille: number; apercu?: string; apercuSur?: SafeUrl; pourcentage: number; erreur?: string; fichier: File; }

/**
 * Ajout de documents : photo (caméra arrière sur mobile) ou fichier, compression des images,
 * progression réelle, classement et lien automatiques (meta).
 *
 * Mode différé (differe=true) : les fichiers sont gardés en attente tant que l'objet
 * n'existe pas encore (nouvelle dépense) ; le parent appelle envoyerEnAttente(lien) après l'enregistrement.
 */
@Component({
  selector: 'bay-document-uploader',
  template: `
    <div class="bay-uploader" [class.bay-uploader-compact]="compact">
      <div class="bay-uploader-actions">
        <button type="button" class="bay-btn bay-btn-soft" (click)="camera.click()" [disabled]="occupe">
          <span class="material-symbols-outlined" aria-hidden="true">photo_camera</span>{{ labelPhoto }}
        </button>
        <button type="button" class="bay-btn bay-btn-soft" (click)="fichierInput.click()" [disabled]="occupe">
          <span class="material-symbols-outlined" aria-hidden="true">attach_file</span>{{ labelFichier }}
        </button>
        <button *ngIf="accepteImages" type="button" class="bay-btn bay-btn-soft" (click)="depuisGalerie()" [disabled]="occupe" data-test="uploader-galerie">
          <span class="material-symbols-outlined" aria-hidden="true">photo_library</span>Galerie Wanar
        </button>
        <input #camera type="file" accept="image/*" capture="environment" class="visually-hidden" tabindex="-1"
          aria-hidden="true" (change)="choisis($any($event.target).files, 'PHOTO'); camera.value = ''">
        <input #fichierInput type="file" [accept]="accept" [multiple]="multiple" class="visually-hidden" tabindex="-1"
          aria-hidden="true" (change)="choisis($any($event.target).files, 'UPLOAD'); fichierInput.value = ''">
      </div>
      <p *ngIf="aide && !fichiers.length" class="bay-hint mb-0">{{ aide }}</p>
      <ul *ngIf="fichiers.length" class="bay-uploader-list" aria-live="polite">
        <li *ngFor="let f of fichiers; let i = index" class="bay-uploader-item" [class.bay-uploader-err]="f.erreur">
          <img *ngIf="f.apercuSur" [src]="f.apercuSur" alt="" class="bay-uploader-thumb">
          <span *ngIf="!f.apercuSur" class="material-symbols-outlined bay-uploader-thumb-icon" aria-hidden="true">description</span>
          <span class="min-w-0 flex-grow-1">
            <span class="bay-uploader-nom">{{ f.nom }}</span>
            <span class="bay-uploader-meta">
              <ng-container *ngIf="f.erreur">{{ f.erreur }}</ng-container>
              <ng-container *ngIf="!f.erreur && f.pourcentage === 100">Enregistré</ng-container>
              <ng-container *ngIf="!f.erreur && f.pourcentage < 100 && (occupe || !differe)">Envoi… {{ f.pourcentage }} %</ng-container>
              <ng-container *ngIf="!f.erreur && f.pourcentage === 0 && differe && !occupe">Sera joint à l'enregistrement</ng-container>
            </span>
            <span *ngIf="f.pourcentage > 0 && f.pourcentage < 100" class="bay-progress" role="progressbar"
              [attr.aria-valuenow]="f.pourcentage" aria-valuemin="0" aria-valuemax="100">
              <span [style.width.%]="f.pourcentage"></span>
            </span>
          </span>
          <button *ngIf="f.pourcentage === 0 || f.erreur" type="button" class="bay-icon-btn" (click)="retirer(i)"
            [attr.aria-label]="'Retirer ' + f.nom">
            <span class="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </li>
      </ul>
    </div>
  `
})
export class DocumentUploaderComponent {
  @Input() meta: MetaTeleversement = {};
  @Input() multiple = true;
  @Input() differe = false;
  @Input() compact = false;
  @Input() accept = 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt';
  @Input() labelPhoto = 'Photo';
  @Input() labelFichier = 'Fichier';
  @Input() aide = '';
  @Output() televerse = new EventEmitter<Document>();
  @Output() enAttente = new EventEmitter<number>();

  fichiers: FichierEnCours[] = [];
  occupe = false;

  constructor(private ged: GedService, private fermeState: FermeStateService, private toast: ToastService,
              private sanitizer: DomSanitizer, private selecteur: GalerieSelecteurService) {}

  get accepteImages(): boolean { return /image/.test(this.accept); }

  /** Photo de la Galerie : son image d'affichage est envoyée comme un nouveau fichier, l'originale reste dans la Galerie */
  async depuisGalerie(): Promise<void> {
    try {
      const f = await this.selecteur.choisirFichier();
      if (f) { this.choisis([f] as any, 'UPLOAD'); }
    } catch { this.toast.erreur('La photo n\'a pas pu être récupérée.'); }
  }

  get nbEnAttente(): number { return this.fichiers.filter(f => f.pourcentage === 0 && !f.erreur).length; }

  choisis(liste: FileList | File[] | null, origine: 'PHOTO' | 'UPLOAD'): void {
    const nouveaux = Array.from(liste || []);
    if (!nouveaux.length) return;
    if (!this.multiple) this.fichiers = this.fichiers.filter(f => f.pourcentage === 100);
    nouveaux.forEach(f => {
      const item: FichierEnCours = { nom: f.name, taille: f.size, pourcentage: 0, fichier: f };
      if (f.type.startsWith('image/')) {
        item.apercu = URL.createObjectURL(f);
        item.apercuSur = this.sanitizer.bypassSecurityTrustUrl(item.apercu);   // blob: local, créé ici
      }
      (item as any).origine = origine;
      this.fichiers.push(item);
    });
    this.enAttente.emit(this.nbEnAttente);
    if (!this.differe) this.envoyerEnAttente().subscribe();
  }

  retirer(i: number): void {
    const f = this.fichiers[i];
    if (f.apercu) URL.revokeObjectURL(f.apercu);
    this.fichiers.splice(i, 1);
    this.enAttente.emit(this.nbEnAttente);
  }

  /** Envoie les fichiers en attente ; lien facultatif (objet créé entre-temps). Renvoie les documents créés. */
  envoyerEnAttente(lien: Partial<MetaTeleversement> = {}): Observable<Document[]> {
    const aEnvoyer = this.fichiers.filter(f => f.pourcentage === 0 && !f.erreur);
    if (!aEnvoyer.length) return of([]);
    this.occupe = true;
    const envois = aEnvoyer.map(f => from(compresserImage(f.fichier)).pipe(
      switchMap(({ blob, nom }) => this.ged.televerser(this.fermeState.fermeId, blob, nom, {
        ...this.meta, ...lien,
        nom: this.meta.nom && aEnvoyer.length === 1 ? this.meta.nom : (lien.nom || f.nom.replace(/\.[^.]+$/, '')),
        origine: (f as any).origine,
        // La compression perd la date du fichier : date de prise transmise à part
        datePrise: f.fichier.type.startsWith('image/') && f.fichier.lastModified ? dateLocaleIso(f.fichier.lastModified) : undefined
      })),
      tap(p => { f.pourcentage = p.pourcentage; }),
      last(),
      map(p => { if (p.document) this.televerse.emit(p.document); return p.document as Document; }),
      catchError(err => {
        f.erreur = err?.error?.message || 'Envoi impossible';
        f.pourcentage = 0;
        return of(null as any);
      })
    ));
    return forkJoin(envois).pipe(
      map(docs => docs.filter(d => !!d)),
      tap(docs => {
        this.occupe = false;
        this.enAttente.emit(this.nbEnAttente);
        const echecs = aEnvoyer.filter(f => f.erreur).length;
        if (echecs) this.toast.erreur(`${echecs} fichier(s) non envoyé(s) : réessayez depuis la fiche.`);
      })
    );
  }

  vider(): void {
    this.fichiers.forEach(f => f.apercu && URL.revokeObjectURL(f.apercu));
    this.fichiers = [];
    this.enAttente.emit(0);
  }
}
